"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteSidebar } from "@/components/SiteSidebar";
import {
  api,
  loadTokens,
  type TicketRecord,
  type TicketStatus,
} from "@/lib/api";

/**
 * STORY-661 — caller's own help-desk tickets list. Logged-out users
 * → /login?next=/help/mine.
 */
export default function MyTicketsPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const [tickets, setTickets] = useState<TicketRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loadTokens()) {
      router.replace("/login?next=" + encodeURIComponent("/help/mine"));
      return;
    }
    let cancelled = false;
    api
      .listMyTickets({ limit: 100 })
      .then((res) => {
        if (cancelled) return;
        setTickets(res.tickets);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : t("helpMine.errLoad"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [router]);

  return (
    <>
      <SiteHeader />
      <div className="mx-auto grid w-full max-w-[1400px] grid-cols-1 gap-5 px-5 py-4 md:grid-cols-[200px_1fr]">
        <SiteSidebar />
        <main className="min-w-0">
          <header className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {t("helpMine.title")}
              </h1>
              <p className="mt-1 text-[12px] text-[var(--color-text3)]">
                {t("helpMine.subtitle")}
              </p>
            </div>
            <Link
              href="/help/new"
              className="inline-flex items-center justify-center rounded-md px-4 py-2 text-[12px] font-semibold text-white"
              style={{ background: "var(--color-brand)" }}
            >
              {t("helpMine.newTicketCta")}
            </Link>
          </header>

          {error && (
            <div
              role="alert"
              className="mb-4 rounded-md border px-3 py-2 text-sm"
              style={{
                borderColor:
                  "color-mix(in srgb, var(--color-status-negative) 30%, transparent)",
                background:
                  "color-mix(in srgb, var(--color-status-negative) 8%, transparent)",
                color: "var(--color-status-negative)",
              }}
            >
              {error}
            </div>
          )}

          {loading ? (
            <p className="text-sm text-[var(--color-text3)]">
              {t("helpMine.loading")}
            </p>
          ) : tickets.length === 0 ? (
            <div
              className="card p-8 text-center"
              style={{ background: "var(--color-surface)" }}
            >
              <p className="text-[14px] font-semibold">
                {t("helpMine.emptyTitle")}
              </p>
              <p className="mt-1 text-[12px] text-[var(--color-text3)]">
                {t("helpMine.emptyBody")}
              </p>
              <Link
                href="/help/new"
                className="mt-4 inline-flex items-center justify-center rounded-md px-4 py-2 text-[12px] font-semibold text-white"
                style={{ background: "var(--color-brand)" }}
              >
                {t("helpMine.openFirstCta")}
              </Link>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {tickets.map((ticket) => (
                <Link
                  key={ticket.id}
                  href={`/help/mine/${ticket.id}`}
                  className="card flex items-center gap-3 p-3 transition-colors hover:border-[var(--color-border2)]"
                  style={{ background: "var(--color-surface)" }}
                >
                  <StatusPill status={ticket.status} t={t} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-semibold">
                      {ticket.subject}
                    </div>
                    <div className="mt-0.5 truncate text-[11px] text-[var(--color-text3)]">
                      {ticket.category} ·{" "}
                      {t("helpMine.createdLabel", {
                        rel: fmtRelative(ticket.createdAt, t),
                      })}
                      {ticket.lastAdminResponseAt && (
                        <>
                          {" · "}
                          {t("helpMine.lastAdminReplyLabel", {
                            rel: fmtRelative(ticket.lastAdminResponseAt, t),
                          })}
                        </>
                      )}
                    </div>
                  </div>
                  <span className="text-[var(--color-text3)]">→</span>
                </Link>
              ))}
            </div>
          )}
        </main>
      </div>
    </>
  );
}

function StatusPill({
  status,
  t,
}: {
  status: TicketStatus;
  t: TFunction;
}) {
  const map: Record<TicketStatus, { label: string; bg: string; fg: string }> =
    {
      open: {
        label: t("helpMine.status.open"),
        bg: "color-mix(in srgb, var(--color-status-info) 15%, transparent)",
        fg: "var(--color-status-info)",
      },
      in_progress: {
        label: t("helpMine.status.inProgress"),
        bg: "color-mix(in srgb, var(--color-status-warning) 15%, transparent)",
        fg: "var(--color-status-warning)",
      },
      waiting_user: {
        label: t("helpMine.status.waitingUser"),
        bg: "color-mix(in srgb, var(--color-status-caution) 15%, transparent)",
        fg: "var(--color-status-caution)",
      },
      resolved: {
        label: t("helpMine.status.resolved"),
        bg: "color-mix(in srgb, var(--color-status-positive) 15%, transparent)",
        fg: "var(--color-status-positive)",
      },
      closed: {
        label: t("helpMine.status.closed"),
        bg: "color-mix(in srgb, var(--color-text3) 15%, transparent)",
        fg: "var(--color-text3)",
      },
    };
  const s = map[status];
  return (
    <span
      className="inline-block flex-shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.3px]"
      style={{ background: s.bg, color: s.fg }}
    >
      {s.label}
    </span>
  );
}

function fmtRelative(ts: string, t: TFunction): string {
  const now = Date.now();
  const then = new Date(ts).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) return t("helpMine.relativeJustNow");
  if (minutes < 60) return t("helpMine.relativeMinutes", { n: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("helpMine.relativeHours", { n: hours });
  const days = Math.floor(hours / 24);
  return t("helpMine.relativeDays", { n: days });
}
